import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import HRMInputFieldType from "../classes/HRMInputFieldType";
import ListWrapper from "../utils/ListWrapper";
import LabelWithHelp from "./LabelWithHelp";
import TextView from "./TextView";
import Duration from "../core/Duration";
import Timer from "../utils/Timer";
import { BuildContext } from "../classes/BuildContext";

type _LabelWithStringFieldOnChanged = (value: string) => void;

type _LabelWithStringFieldOnTap = () => void;

type _LabelWithStringFieldOnEditingComplete = (value: string) => void;

type _LabelWithStringFieldOnSubmitted = (value: string) => void;

export interface LabelWithStringFieldProps extends BaseUIProps {
  key?: string;
  name?: string;
  help?: string;
  helpUrl?: string;
  placeHolder?: string;
  isRequired?: boolean;
  value?: string;
  errors?: Array<string>;
  fieldType?: HRMInputFieldType;
  obscureText?: boolean;
  disable?: boolean;
  fieldColor?: ui.Color;
  autoFocus?: boolean;
  _errorsHash?: number;
  onChanged?: _LabelWithStringFieldOnChanged;
  onTap?: _LabelWithStringFieldOnTap;
  onEditingComplete?: _LabelWithStringFieldOnEditingComplete;
  onSubmitted?: _LabelWithStringFieldOnSubmitted;
}

class _LabelWithStringFieldState extends ObservableComponent<LabelWithStringFieldProps> {
  static defaultProps = {
    name: "",
    help: "",
    helpUrl: "",
    placeHolder: "",
    isRequired: false,
    value: "",
    fieldType: HRMInputFieldType.Rectangle,
    obscureText: false,
    disable: false,
    fieldColor: null,
    autoFocus: false,
    errors: [],
    onChanged: null,
    onTap: null,
    onEditingComplete: null,
    onSubmitted: null,
  };
  fieldController: ui.TextEditingController = new ui.TextEditingController();
  focusNode: ui.FocusNode = null;
  active: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: LabelWithStringFieldProps) {
    super(props);

    this.initState();
  }
  public get name(): string {
    return this.props.name;
  }
  public get help(): string {
    return this.props.help;
  }
  public get helpUrl(): string {
    return this.props.helpUrl;
  }
  public get placeHolder(): string {
    return this.props.placeHolder;
  }
  public get isRequired(): boolean {
    return this.props.isRequired;
  }
  public get value(): string {
    return this.props.value;
  }
  public get errors(): Array<string> {
    return this.props.errors;
  }
  public get fieldType(): HRMInputFieldType {
    return this.props.fieldType;
  }
  public get obscureText(): boolean {
    return this.props.obscureText;
  }
  public get disable(): boolean {
    return this.props.disable;
  }
  public get fieldColor(): ui.Color {
    return this.props.fieldColor;
  }
  public get autoFocus(): boolean {
    return this.props.autoFocus;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.subscribeToList(this.errors, "errors");

    this.on(
      [
        "active",
        "autoFocus",
        "disable",
        "errors",
        "fieldType",
        "focusNode",
        "help",
        "helpUrl",
        "isRequired",
        "name",
        "obscureText",
        "placeHolder",
        "value",
      ],
      this.rebuild
    );

    this.on(["value"], () => {
      this.fieldController.text = this.value;
    });
  }
  public componentDidUpdate(prevProps: LabelWithStringFieldProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.name !== this.props.name) {
      this.fire("name", this);
    }

    if (prevProps.help !== this.props.help) {
      this.fire("help", this);
    }

    if (prevProps.helpUrl !== this.props.helpUrl) {
      this.fire("helpUrl", this);
    }

    if (prevProps.placeHolder !== this.props.placeHolder) {
      this.fire("placeHolder", this);
    }

    if (prevProps.isRequired !== this.props.isRequired) {
      this.fire("isRequired", this);
    }

    if (prevProps.value !== this.props.value) {
      this.fire("value", this);
    }

    if (prevProps.errors !== this.props.errors) {
      this.fire("errors", this);
    }

    if (prevProps.fieldType !== this.props.fieldType) {
      this.fire("fieldType", this);
    }

    if (prevProps.obscureText !== this.props.obscureText) {
      this.fire("obscureText", this);
    }

    if (prevProps.disable !== this.props.disable) {
      this.fire("disable", this);
    }

    if (prevProps.fieldColor !== this.props.fieldColor) {
      this.fire("fieldColor", this);
    }

    if (prevProps.autoFocus !== this.props.autoFocus) {
      this.fire("autoFocus", this);
    }
  }
  public setFocusNode(val: ui.FocusNode): void {
    let isValChanged: boolean = this.focusNode !== val;

    if (!isValChanged) {
      return;
    }

    this.focusNode = val;

    this.fire("focusNode", this);
  }
  public setActive(val: boolean): void {
    let isValChanged: boolean = this.active !== val;

    if (!isValChanged) {
      return;
    }

    this.active = val;

    this.fire("active", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          children: [
            this.name !== null && this.name.isNotEmpty
              ? LabelWithHelp({
                  label: this.name,
                  help: this.help,
                  helpUrl: this.helpUrl,
                })
              : [],
            this.isRequired
              ? TextView({
                  data: "*",
                  textAlign: cStyle.tTextViewLabelTextTextAlignOn,
                  style: new ui.TextStyle({
                    color: cStyle.tTextViewLabelTextColorOn,
                    fontSize: cStyle.tTextViewLabelTextFontSizeOn,
                    fontWeight: cStyle.tTextViewLabelTextFontWeightOn,
                  }),
                  className: "x48",
                })
              : [],
          ],
          className: "x25b hc h",
          key: "0",
        }),
        ui.InputField({
          value: this.value,
          focusNode: this.focusNode,
          obscureText: this.obscureText,
          autofocus: this.autoFocus,
          disable: this.disable,
          placeHolder: this.placeHolder,
          cornerRadius:
            this.fieldType === HRMInputFieldType.Circle && this.active
              ? 0
              : this.fieldType === HRMInputFieldType.Circle && !this.active
              ? 0
              : this.fieldType === HRMInputFieldType.Underline && this.active
              ? 0
              : 0,
          backgroundColor: cStyle.tInputFieldNormalInputfieldBackgroundColorOn,
          style: cStyle.tInputFieldNormalInputfieldStyleOn,
          inActiveColor: this.errors.isNotEmpty
            ? cStyle.c11
            : this.fieldType === HRMInputFieldType.Circle && !this.active
            ? cStyle.c7
            : cStyle.tInputFieldNormalInputfieldInActiveColorOn,
          placeHolderColor:
            cStyle.tInputFieldNormalInputfieldPlaceHolderColorOn,
          cursorColor: cStyle.tInputFieldNormalInputfieldCursorColorOn,
          controller: this.fieldController,
          onChanged: (text) => {
            this.onChangeText(text);
          },
          onEditingComplete: () => {
            this.onFieldEditing();
          },
          onSubmitted: (text) => {
            this.onFieldSubmitted(text);
          },
          onFocusChange: (val) => {},
          states: ui.joinStates(
            {
              "data-c0": this.errors.isNotEmpty,
              "data-c1":
                this.fieldType === HRMInputFieldType.Circle && this.active,
              "data-c2":
                this.fieldType === HRMInputFieldType.Circle && !this.active,
              "data-c3":
                this.fieldType === HRMInputFieldType.Underline && this.active,
            },
            {}
          ),
          activeColor: this.errors.isNotEmpty
            ? cStyle.c11
            : this.fieldType === HRMInputFieldType.Circle && this.active
            ? cStyle.c5
            : null,
          className: "x57 hc vc",
          key: "1",
        }),
        this.errors.isNotEmpty
          ? ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                this.errors.expand((item) => [
                  TextView({
                    data: item,
                    textAlign: cStyle.tTextViewErrorMessageTextAlignOn,
                    style: new ui.TextStyle({
                      color: cStyle.tTextViewErrorMessageColorOn,
                      fontSize: cStyle.tTextViewErrorMessageFontSizeOn,
                    }),
                    className: "x961 hc",
                    key: item?.toString(),
                  }),
                ]),
              ],
              className: "x28 hc",
            })
          : [],
      ],
      className: ui.join(this.props.className, "LabelWithStringField x3e hc h"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setFocusNode(new ui.FocusNode());

    this.focusNode.addListener(this.onChangeFocus);

    if (this.autoFocus) {
      this.focusNode.requestFocus();

      new Timer(new Duration({ milliseconds: 100 }), () => {
        this.fieldController.selection = new ui.TextSelection({
          baseOffset: 0,
          extentOffset: this.value?.length ?? 0,
        });
      });
    }
  };
  public onChangeFocus = (): void => {
    this.setActive(this.focusNode.hasFocus);

    if (!this.active) {
      this.fieldController.selection = new ui.TextSelection({
        baseOffset: 0,
        extentOffset: 0,
      });
    }

    if (this.active && this.onTap !== null) {
      this.onTap();
    }
  };
  public onChangeText = (text: string): void => {
    if (this.onChanged !== null) {
      this.onChanged(text);
    }
  };
  public onFieldEditing = (): void => {
    if (this.onEditingComplete !== null) {
      this.onEditingComplete(this.fieldController.text);
    }
  };
  public onFieldSubmitted = (text: string): void => {
    if (this.onSubmitted !== null) {
      this.onSubmitted(this.fieldController.text);

      this.focusNode.unfocus();
    }
  };
  public get onChanged(): _LabelWithStringFieldOnChanged {
    return this.props.onChanged;
  }
  public get onTap(): _LabelWithStringFieldOnTap {
    return this.props.onTap;
  }
  public get onEditingComplete(): _LabelWithStringFieldOnEditingComplete {
    return this.props.onEditingComplete;
  }
  public get onSubmitted(): _LabelWithStringFieldOnSubmitted {
    return this.props.onSubmitted;
  }
}
export default function LabelWithStringField(props: LabelWithStringFieldProps) {
  return React.createElement(
    _LabelWithStringFieldState,
    { ..._LabelWithStringFieldState.defaultProps, ...props },
    ListWrapper.fromInput<string>(props.errors, "errors")
  );
}
